import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import {
  RECEIPT_RECOVERY_MOCK_ACCOUNTING_PERIODS,
  RECEIPT_RECOVERY_MOCK_STATS_BY_PERIOD,
} from 'src/feature/ReceiptRecovery/receiptRecovery.fixtures'
import { ReceiptRecoveryTransactionState } from 'src/feature/ReceiptRecovery/receiptRecovery.types'
import ReceiptRecoveryWorkspace from 'src/feature/ReceiptRecovery/ReceiptRecoveryWorkspace'
import { useTranslate } from 'src/localization/Trns'
import { TransactionReceiptRecoveryStatus } from 'src/models/transaction.models'
import { accountingPeriodQueries } from 'src/queries/organizations/accountingPeriods/accountingPeriods.queries'
import { ReceiptAgentRun } from 'src/queries/organizations/receiptAgent/receiptAgent.models'
import {
  isReceiptAgentRunActive,
  RECEIPT_AGENT_RUN_IN_PROGRESS_ERROR_CODE,
  receiptAgentMutations,
  receiptAgentQueries,
  receiptAgentQueryKeys,
} from 'src/queries/organizations/receiptAgent/receiptAgent.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationIdFromState } from 'src/redux/selectors/data/organizations.selectors'

import { getReceiptRecoveryTransactionsUrl } from './receiptRecoveryNavigation'

const transactionStatusForReceiptState: Record<
  ReceiptRecoveryTransactionState,
  TransactionReceiptRecoveryStatus
> = {
  'needs-review': TransactionReceiptRecoveryStatus.NeedsReview,
  'waiting-on-spender': TransactionReceiptRecoveryStatus.WaitingOnSpender,
  'receipt-reconciled': TransactionReceiptRecoveryStatus.ReceiptReconciled,
}

const ReceiptRecoveryPage = (): ReactElement => {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>()
  const [runError, setRunError] = useState<string>()
  const [lastStartedRun, setLastStartedRun] = useState<ReceiptAgentRun>()
  const history = useHistory()
  const queryClient = useQueryClient()
  const organizationId = useSelector(selectUserExplicitOrganizationIdFromState)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const translate = useTranslate()
  const dashboardEnabled = remoteConfig.enable_receipt_recovery_dashboard
  const mockDataEnabled = remoteConfig.enable_receipt_recovery_mock_data
  const accountingPeriodsQueryOptions =
    accountingPeriodQueries.list(organizationId)
  const accountingPeriodsQuery = useQuery({
    ...accountingPeriodsQueryOptions,
    enabled:
      accountingPeriodsQueryOptions.enabled &&
      dashboardEnabled &&
      !mockDataEnabled,
  })
  const accountingPeriods = accountingPeriodsQuery.data
  const receiptAgentQueryOptions = receiptAgentQueries.status(organizationId)
  const receiptAgentQuery = useQuery({
    ...receiptAgentQueryOptions,
    enabled:
      receiptAgentQueryOptions.enabled && dashboardEnabled && !mockDataEnabled,
  })
  const periods = mockDataEnabled
    ? RECEIPT_RECOVERY_MOCK_ACCOUNTING_PERIODS
    : (accountingPeriods ?? [])
  const currentPeriod = periods.find((period) => period.current)
  const resolvedSelectedPeriod =
    periods.find((period) => period.id === selectedPeriodId) ?? currentPeriod
  const selectedPeriodStatsQueryOptions = accountingPeriodQueries.stats(
    organizationId,
    resolvedSelectedPeriod?.id,
  )
  const selectedPeriodStatsQuery = useQuery({
    ...selectedPeriodStatsQueryOptions,
    enabled:
      selectedPeriodStatsQueryOptions.enabled &&
      dashboardEnabled &&
      !mockDataEnabled,
  })
  const selectedPeriodStats = selectedPeriodStatsQuery.data
  const receiptAgentRunsQueryOptions = receiptAgentQueries.runs(
    organizationId,
    resolvedSelectedPeriod?.id,
  )
  const receiptAgentRunsQuery = useQuery({
    ...receiptAgentRunsQueryOptions,
    enabled:
      receiptAgentRunsQueryOptions.enabled &&
      dashboardEnabled &&
      !mockDataEnabled,
  })
  const isLastStartedRunActiveForSelectedPeriod =
    lastStartedRun !== undefined &&
    lastStartedRun.accountingPeriodId === resolvedSelectedPeriod?.id &&
    isReceiptAgentRunActive(lastStartedRun)
  const isRunActive = Boolean(
    receiptAgentRunsQuery.data?.find(isReceiptAgentRunActive) ??
      (receiptAgentRunsQuery.data === undefined &&
      isLastStartedRunActiveForSelectedPeriod
        ? lastStartedRun
        : undefined),
  )
  const isRunAvailable =
    !mockDataEnabled &&
    receiptAgentQuery.data?.canRunReceiptRecovery === true &&
    !receiptAgentRunsQuery.isFetching &&
    !receiptAgentRunsQuery.isError &&
    !isRunActive
  const { isPending: isRunPending, mutate: startReceiptAgentRun } = useMutation(
    {
      ...receiptAgentMutations.startRun(),
      onMutate: () => {
        setRunError(undefined)
      },
      onSuccess: (run, { organizationId, accountingPeriodId }) => {
        const queryKey = receiptAgentQueryKeys.runs(
          organizationId,
          accountingPeriodId,
        )
        queryClient.setQueryData<ReceiptAgentRun[]>(queryKey, (runs = []) => [
          run,
          ...runs.filter((existingRun) => existingRun.id !== run.id),
        ])
        setLastStartedRun(run)
        void queryClient.invalidateQueries({ queryKey })
      },
      onError: (error) => {
        setRunError(
          error.errorCode === RECEIPT_AGENT_RUN_IN_PROGRESS_ERROR_CODE
            ? translate(
                'Another Receipt Agent run is already in progress. Wait for it to finish before trying again.',
              )
            : translate('We could not start Receipt Agent. Please try again.'),
        )
      },
    },
  )

  const handleOpenTransactions = useCallback(
    (state: ReceiptRecoveryTransactionState): void => {
      if (!resolvedSelectedPeriod) return

      history.push(
        getReceiptRecoveryTransactionsUrl(
          transactionStatusForReceiptState[state],
          resolvedSelectedPeriod,
        ),
      )
    },
    [history, resolvedSelectedPeriod],
  )

  const handleRunReceiptRecovery = useCallback((): void => {
    if (!isRunAvailable || !organizationId || !resolvedSelectedPeriod) return
    startReceiptAgentRun({
      organizationId,
      accountingPeriodId: resolvedSelectedPeriod.id,
    })
  }, [
    isRunAvailable,
    organizationId,
    resolvedSelectedPeriod,
    startReceiptAgentRun,
  ])

  const mockStats =
    mockDataEnabled && resolvedSelectedPeriod
      ? RECEIPT_RECOVERY_MOCK_STATS_BY_PERIOD[resolvedSelectedPeriod.id]
      : undefined
  const receiptStats = mockDataEnabled
    ? mockStats
    : selectedPeriodStats?.closingStats.receiptStats

  if (!dashboardEnabled) {
    return <NotFoundBackgroundPage />
  }
  if (
    !mockDataEnabled &&
    (accountingPeriodsQuery.isError ||
      selectedPeriodStatsQuery.isError ||
      (!accountingPeriodsQuery.isPending && !resolvedSelectedPeriod))
  ) {
    return (
      <LayoutShell
        content={
          <AlertPanel role="alert" variant="severe">
            {translate('We could not load Receipt Agent. Please try again.')}
          </AlertPanel>
        }
        subnavDesign={SubnavDesign.Floating}
        subnavOptions={[]}
        title={
          <span role="heading" aria-level={1}>
            {translate('Receipt Agent')}
          </span>
        }
      />
    )
  }
  if (!resolvedSelectedPeriod || !receiptStats) {
    return <LoadingBackgroundPage />
  }

  return (
    <LayoutShell
      buttons={[
        <Button
          key="run-receipt-recovery"
          disabled={!isRunAvailable || isRunPending || isRunActive}
          loading={isRunPending}
          style={{ alignSelf: 'center' }}
          variant="primary"
          onClick={handleRunReceiptRecovery}
        >
          {translate(
            isRunActive ? 'Running recovery...' : 'Run receipt recovery',
          )}
        </Button>,
      ]}
      content={
        <ReceiptRecoveryWorkspace
          accountingPeriods={periods}
          isRunActive={isRunActive}
          onOpenTransactions={handleOpenTransactions}
          onPeriodSelect={setSelectedPeriodId}
          receiptStats={receiptStats}
          runError={runError}
          selectedPeriodId={resolvedSelectedPeriod.id}
        />
      }
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={[]}
      subtitle={translate('${period} in view', {
        period: resolvedSelectedPeriod.label,
      })}
      title={
        <span role="heading" aria-level={1}>
          {translate('Receipt Agent')}
        </span>
      }
    />
  )
}

export default ReceiptRecoveryPage
